import { DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BackButton, MainButton, View } from '@ds'
import {
  MainLayout,
  useStarAnim,
  useStripesAnim,
  useIsQuestCompleted,
  FLOWERS_QUEST_ITEM_ID,
  DOTS_QUEST_ITEM_ID,
  GalaxyButton,
  CHANGES_QUEST_ITEM_ID,
  VOLUME_QUEST_ITEM_ID,
} from '@shared'
import { QuestId, useShop, useShopActions, useOverlayActions } from '@store'

import styles from './Shop.module.scss'
import shopBackground from './assets/comp.png'
import { Booster } from './booster/Booster'
import { Category } from './category/Category'
import { BatchPurchase, BoxPixiAnimation } from './batchPurchase'
import { GetFreeItem } from './getFreeItem'

const FREE_OFFERS = '30% off'
const LAST_SPIRAL_VESSEL_ID = '019e1cd5-fdd2-7c24-8ebd-c17c7e6665aa'

const getClassNameByItem = (name: string) => {
  if (name.startsWith('Moonlove')) {
    return styles.epicBorder
  }
  if (name.startsWith('Notcoin')) {
    return styles.legendaryBorder
  }
  return undefined
}

export const Shop = () => {
  const navigate = useNavigate()
  const { starAnim } = useStarAnim()
  const { stripesAnim } = useStripesAnim()
  const { getStoreItems } = useShopActions()
  const { setOverlayQueue } = useOverlayActions();
  const items = useShop((state) => state.items)
  const isFlowersQuestCompleted = useIsQuestCompleted(QuestId.Flowers)
  const isDotsQuestCompleted = useIsQuestCompleted(QuestId.Dots)
  const isChangesQuestCompleted = useIsQuestCompleted(QuestId.Changes)
  const isVolumeQuestCompleted = useIsQuestCompleted(QuestId.Volume)

  useEffect(() => {
    getStoreItems()
  }, [])

  const tapRefillItem = items?.find((item) => item.name === 'Tap refill')
  const boosters = items?.filter(
    (item) =>
      item.type === 'item' &&
      !item.name.includes('vessel') &&
      item.name !== '019b3d56-be10-722c-983f-151cbd275af1' &&
      item.name !== 'Tap refill' &&
      (item.id === FLOWERS_QUEST_ITEM_ID ? isFlowersQuestCompleted : true) &&
      (item.id === DOTS_QUEST_ITEM_ID ? isDotsQuestCompleted : true) &&
      (item.id === CHANGES_QUEST_ITEM_ID ? isChangesQuestCompleted : true) &&
      (item.id === VOLUME_QUEST_ITEM_ID ? isVolumeQuestCompleted : true)
  )

  const collectionPack = items?.find(i => i.id === '019b3d56-be10-722c-983f-151cbd275af1')

  const lastSpiralVessel = items?.find(i => i.id === LAST_SPIRAL_VESSEL_ID)

  const lootBox = items?.filter(
    (item) =>
      item.name.includes('vessel') && item.id !== LAST_SPIRAL_VESSEL_ID
  ) || [];

  const { t } = useTranslation()

  return (
    <MainLayout showInventory={false} isNavbarFadeble>
      {(scrollRef) => (
        <>
          <BackButton onClick={() => navigate('/')} />
          <MainButton hidden />

          <View fadeIn className={styles.view} ref={scrollRef}>
            <img className={styles.comp} src={shopBackground} />
            {stripesAnim && (
              <>
                <DotLottieWorkerReact
                  data={stripesAnim}
                  autoplay
                  loop
                  width={140}
                  height={140}
                  className={styles.compStripesRight}
                />
                <DotLottieWorkerReact
                  data={stripesAnim}
                  autoplay
                  loop
                  width={140}
                  height={140}
                  className={styles.compStripesLeft}
                />
              </>
            )}
            {starAnim && (
              <>
                <DotLottieWorkerReact
                  data={starAnim}
                  autoplay
                  loop
                  width={100}
                  height={100}
                  className={styles.compStarsRight}
                />
                <DotLottieWorkerReact
                  data={starAnim}
                  autoplay
                  loop
                  width={100}
                  height={100}
                  className={styles.compStarsLeft}
                />
              </>
            )}
            <div className={styles.dispatcher}>{t('shop.dispatcher')}</div>
            <div className={styles.description}>{t('shop.welcome')}</div>
            <GalaxyButton
              withGlow={false}
              className={styles.donate}
              onClick={() => {
                setOverlayQueue([{ type: 'donate' }])
              }}
            >
              Donate
            </GalaxyButton>
            {lastSpiralVessel && (
              <BatchPurchase
                className={styles.lastSpiralVesselBorder}
                title={lastSpiralVessel.name}
                discount={FREE_OFFERS}
                price={lastSpiralVessel.prices['XTR']}
                defaultQuantity={1}
                limitation={`${lastSpiralVessel.left_supply}/${lastSpiralVessel.supply}`}
                diamondColor="rgba(115, 0, 255, 1)"
                imageSlot={<BoxPixiAnimation className={styles.lastSpiralVesselImage} />}
                onChangePopupPreset={(quantity) => {
                  setOverlayQueue([
                    {
                      type: 'payment',
                      payload: {
                        item: {
                          ...lastSpiralVessel,
                          prices: {
                            ...lastSpiralVessel.prices,
                            quantity,
                          },
                        },
                      },
                    },
                  ])
                }}
              />
            )}
            {collectionPack && <BatchPurchase
              className={styles.collectionPack}
              imgSrc={collectionPack.image_url}
              defaultQuantity={1}
              discount={FREE_OFFERS}
              limitation={`${collectionPack.left_supply}/${collectionPack.supply}`}
              title={collectionPack.name}
              price={collectionPack.prices['XTR']}
              onChangePopupPreset={(quantity) => {
                setOverlayQueue([
                  {
                    type: 'payment',
                    payload: {
                      item: {
                        ...collectionPack,
                        prices: {
                          ...collectionPack.prices,
                          quantity,
                        },
                      },
                    },
                  },
                ])
              }}
            />}
            {/* <GetFreeItem dailyLimit={dailyLimit} label="Get 1 tap refill by watching ads" onClick={() => showAd("refill-shop")} /> */}
            {tapRefillItem && (
              <BatchPurchase
                imgSrc="/assets/shop/finger-refill.png"
                discount={FREE_OFFERS}
                title={t('shop.tapsRefill.title')}
                description={t('shop.tapsRefill.description')}
                price={tapRefillItem.prices['XTR']}
                onChangePopupPreset={(quantity) => {
                  setOverlayQueue([
                    {
                      type: 'payment',
                      payload: {
                        item: {
                          ...tapRefillItem,
                          prices: {
                            ...tapRefillItem.prices,
                            quantity,
                          },
                        },
                      },
                    },
                  ])
                }}
              />
            )}

            <GetFreeItem label={t('ad.getFreeItem')} />

            <Category title="Boosters" count={3} />
            <div className={styles.boosters}>
              {boosters?.map((item, index) => {
                return (
                  <Booster
                    free={FREE_OFFERS}
                    key={item.id}
                    imgPath={item.image_url}
                    title={item.name}
                    price={item.prices['XTR']}
                    movementAnimationDelay={index * 0.5}
                    onClick={() => {
                      setOverlayQueue([
                        {
                          type: 'payment',
                          payload: {
                            item,
                          },
                        },
                      ])
                    }}
                  />
                )
              })}
            </div>
            {/* <Category title="Secrets" count={6} className={styles.secrets} /> */}
            {/* <Secrets
              className={styles.secrets}
              elements={[
                {
                  title: 'Quest Solution',
                  price: 100,
                  spineName: 'eye',
                  imagePath:
                    'https://not-platform-dev.fra1.cdn.digitaloceanspaces.com/item/65b48294-52b9-4234-84da-5330c0422319.png',
                },
                {
                  title: 'Lotus Flower',
                  price: 100,
                  spineName: 'flower',
                  imagePath:
                    'https://not-platform-dev.fra1.cdn.digitaloceanspaces.com/item/65b48294-52b9-4234-84da-5330c0422319.png',
                },
              ]}
            /> */}
            {/* <Divider className={styles.dividerAfterSecrets} /> */}
            <div className={styles.lootBoxes}>
              {lootBox.map((item, index) => (
                <BatchPurchase
                  key={index}
                  className={getClassNameByItem(item.name)}
                  defaultQuantity={item.name.startsWith("Moonlove") || item.name.startsWith('Notcoin') ? 1 : undefined}
                  imgSrc={item.image_url}
                  discount={FREE_OFFERS}
                  limitation={`${item.left_supply}/${item.supply}`}
                  title={item.name}
                  diamondColor={item.name.startsWith('Notcoin') ? '#FFC42F' : undefined}
                  price={item.prices['XTR']}
                  onChangePopupPreset={(quantity) => {
                    setOverlayQueue([
                      {
                        type: 'payment',
                        payload: {
                          item: {
                            ...item,
                            prices: {
                              ...item.prices,
                              quantity,
                            },
                          },
                        },
                      },
                    ])
                  }}
                />
              ))}
            </div>
          </View>
        </>
      )}
    </MainLayout>
  )
}
